import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../types/user';
import { AuthModal } from './auth/AuthModal';
import { WaitlistModal } from './auth/WaitlistModal';
import { Navbar } from './landing/Navbar';
import { Hero } from './landing/Hero';
import { ValueProps } from './landing/ValueProps';
import { AppShowcase } from './landing/AppShowcase';
import { Testimonials } from './landing/Testimonials';
import { UniversityPartnership } from './landing/UniversityPartnership';
import { Compliance } from './landing/Compliance';
import { Footer } from './landing/Footer';

interface TeleTherapyLandingProps {
  onPlanSession: () => void;
  onLogout: () => void;
  isWaitlistModalOpen: boolean;
  setIsWaitlistModalOpen: (open: boolean) => void;
}

const TeleTherapyLanding = ({ onPlanSession, onLogout, isWaitlistModalOpen, setIsWaitlistModalOpen }: TeleTherapyLandingProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [waitlistPrefillEmail, setWaitlistPrefillEmail] = useState('');

  const loadUserData = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      }
    }
  }, []);

  // Load user data on mount and storage changes
  useEffect(() => {
    loadUserData();

    // Listen for storage changes (in case of multiple tabs)
    window.addEventListener('storage', loadUserData);
    return () => window.removeEventListener('storage', loadUserData);
  }, [loadUserData]);

  // Deep link for marketing CTAs (e.g. social posts) to jump straight to the
  // waitlist signup form instead of the full homepage: /?waitlist=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('waitlist') === 'true') {
      setIsWaitlistModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseAuth = () => {
    setIsAuthModalOpen(false);
    // Refresh user data when modal closes
    loadUserData();
  };

  const handleLogout = () => {
    onLogout();
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <Navbar
        user={user}
        onLogout={handleLogout}
        onOpenAuth={() => setIsAuthModalOpen(true)}
      />

      <main className="flex-grow">
        <Hero onPlanSession={onPlanSession} />
        <ValueProps />
        <AppShowcase />
        <Testimonials />
        <UniversityPartnership />
      </main>

      <Compliance />
      <Footer />

      <div className="relative z-50">
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={handleCloseAuth}
          onJoinWaitlist={(email) => {
            setWaitlistPrefillEmail(email);
            setIsAuthModalOpen(false);
            setIsWaitlistModalOpen(true);
          }}
        />

        <WaitlistModal
          isOpen={isWaitlistModalOpen}
          onClose={() => setIsWaitlistModalOpen(false)}
          initialEmail={waitlistPrefillEmail}
        />
      </div>
    </div>
  );
};

export default TeleTherapyLanding;